import { GOAL_METRICS, withMonth, type Goal } from '@finsim/model';
import { useUnit } from 'effector-react';
import { Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { $project, commandIssued, KeyDateMonthField } from '../../entities/project';
import { currencySymbol } from '../../shared/lib/format';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import {
  Button,
  Field,
  IconButton,
  MoneyField,
  Section,
  Select,
  Switch,
  TextField,
  Tooltip,
  EditOnly,
} from '../../shared/ui';
import { goalAddRequested } from './model';

/** Goals ("net worth ≥ X by date"), whose probability Monte Carlo runs report. */
export function GoalsSection() {
  const { t } = useTranslation();
  const locale = useLocale();
  const [project, issue, add] = useUnit([$project, commandIssued, goalAddRequested]);
  if (!project) return null;
  const symbol = currencySymbol(project.assumptions.currency, locale);
  const update = (goal: Goal, field: string): void => {
    issue({ command: { type: 'goal/update', goal }, coalesceKey: `${goal.id}:${field}` });
  };

  return (
    <Section
      title={t('goals.title')}
      actions={
        <EditOnly>
          {project.goals.length < 50 && (
            <Button
              size="sm"
              variant="ghost"
              leadingIcon={<Plus />}
              onClick={() => {
                add({ name: t('goals.newName', { n: project.goals.length + 1 }) });
              }}
            >
              {t('goals.add')}
            </Button>
          )}
        </EditOnly>
      }
    >
      <p className="text-xs text-text-3">{t('goals.description')}</p>
      {project.goals.map((goal) => (
        <fieldset
          key={goal.id}
          aria-label={goal.name}
          className="space-y-2 rounded-md border border-border-subtle p-2.5"
        >
          <div className="flex items-center gap-1.5">
            <TextField
              aria-label={t('goals.name')}
              className="min-w-0 flex-1"
              value={goal.name}
              onCommit={(name) => {
                update({ ...goal, name }, 'name');
              }}
            />
            <EditOnly>
              <Tooltip content={t('goals.remove', { name: goal.name })}>
                <IconButton
                  size="md"
                  label={t('goals.remove', { name: goal.name })}
                  onClick={() => {
                    issue({ command: { type: 'goal/remove', id: goal.id } });
                  }}
                >
                  <Trash2 />
                </IconButton>
              </Tooltip>
            </EditOnly>
          </div>
          <Field label={t('goals.metric')}>
            {({ id }) => (
              <Select
                id={id}
                value={goal.metric}
                options={GOAL_METRICS.map((m) => ({ value: m, label: t(`goals.metrics.${m}`) }))}
                onValueChange={(metric) => {
                  update({ ...goal, metric }, 'metric');
                }}
              />
            )}
          </Field>
          <Field label={t('goals.target')}>
            {({ id }) => (
              <MoneyField
                id={id}
                value={goal.target}
                symbol={symbol}
                min={-1e12}
                onCommit={(target) => {
                  update({ ...goal, target }, 'target');
                }}
              />
            )}
          </Field>
          <Field label={t('goals.by')}>
            {({ id }) => (
              <KeyDateMonthField
                id={id}
                aria-label={`${goal.name} — ${t('goals.by')}`}
                value={goal.by}
                anchor={goal.anchors?.by}
                start={project.assumptions.start}
                max={project.assumptions.horizonMonths - 1}
                onCommit={(by, anchor) => {
                  update(withMonth(goal, 'by', by, anchor), 'by');
                }}
              />
            )}
          </Field>
          <Field label={t('goals.real')}>
            {({ id }) => (
              <div className="flex h-7 items-center">
                <Switch
                  id={id}
                  checked={goal.real}
                  onCheckedChange={(real) => {
                    update({ ...goal, real }, 'real');
                  }}
                />
              </div>
            )}
          </Field>
        </fieldset>
      ))}
    </Section>
  );
}
